import { useMutation, useQueries } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';

import { ApiError } from 'common/src/exceptions/ErrorHandling';
import { WorkOrderSource } from 'common/src/types/enums/WorkOrderSource.enum';
import {
  IAccount,
  IEnergyAsset,
  IFacility,
  IMeter,
  IPage,
  IUser,
  IWorkOrder,
} from 'common/src/types/interfaces';

import { getAccounts } from 'portal/src/api/AccountsService';
import { getEnergyAssets } from 'portal/src/api/EnergyAssetsService';
import { getFacilities } from 'portal/src/api/FacilitiesService';
import { getMeters } from 'portal/src/api/MetersService';
import { getUsersService } from 'portal/src/api/UsersService';
import { addWorkOrder, updateWorkOrder } from 'portal/src/api/WorkOrderService';
import { QUERY_KEYS } from 'portal/src/api/constants';
import useAuth from 'portal/src/auth/useAuth';
import { FormContainer } from 'portal/src/components/common/forms/FormContainer';
import { formControlErrors } from 'portal/src/components/common/forms/formControlErrors';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

import { defaultValues, getFields } from './WorkOrderForm.fields';

const WorkOrderForm = ({
  entity = {},
  handleBackAction,
}: {
  entity?: Partial<IWorkOrder>;
  handleBackAction: () => void;
}) => {
  const { getUser } = useAuth();
  const { organizationId } = getUser();
  const snackbarHandler = useSnackbarHandler();

  const {
    handleSubmit,
    formState: { isDirty },
    control,
    ...formControl
  } = useForm({
    defaultValues: defaultValues(entity as Partial<IWorkOrder>),
  });

  const [
    { data: accounts },
    { data: energyAssets },
    { data: facilities },
    { data: meters },
    { data: users },
  ] = useQueries({
    queries: [
      {
        queryKey: [QUERY_KEYS.accounts],
        queryFn: async () => await getAccounts(),
        initialData: { data: [] } as IPage<IAccount>,
      },
      {
        queryKey: ['energyAssets'],
        queryFn: async () => await getEnergyAssets(),
        initialData: { data: [] } as IPage<IEnergyAsset>,
      },
      {
        queryKey: ['facilities'],
        queryFn: async () => await getFacilities(),
        initialData: { data: [] } as IPage<IFacility>,
      },
      {
        queryKey: [QUERY_KEYS.meters],
        queryFn: async () => await getMeters(),
        initialData: { data: [] } as IPage<IMeter>,
      },
      {
        queryKey: ['users'],
        queryFn: async () => await getUsersService(),
        initialData: { data: [] } as IPage<IUser>,
      },
    ],
  });

  const { mutate: addOrUpdateWorkOrder, isPending } = useMutation({
    mutationFn: async (payload: any) => {
      const accountsPayload = [];
      payload.accounts?.forEach((aId) => {
        const a = accounts.data.find((d) => d.id === aId);
        if (a) {
          accountsPayload.push(a);
        }
      });

      const energyAssetsPayload = [];
      payload.energyAssets?.forEach((eaId) => {
        const ea = energyAssets.data.find((d) => d.id === eaId);
        if (ea) {
          energyAssetsPayload.push(ea);
        }
      });
      const facilitiesPayload = [];
      payload.facilities?.forEach((fId) => {
        const f = facilities.data.find((d) => d.id === fId);
        if (f) {
          facilitiesPayload.push(f);
        }
      });
      const metersPayload = [];
      payload.meters?.forEach((mId) => {
        const m = meters.data.find((d) => d.id === mId);
        if (m) {
          metersPayload.push(m);
        }
      });

      const formattedPayload = {
        ...payload,
        organizationId: entity.organizationId ?? organizationId,
        accounts: accountsPayload,
        energyAssets: energyAssetsPayload,
        facilities: facilitiesPayload,
        meters: metersPayload,
        source: WorkOrderSource.Manual,
      };

      async function add() {
        await addWorkOrder(formattedPayload);
      }

      async function update() {
        await updateWorkOrder(entity.id, formattedPayload);
      }

      await (entity.id ? update() : add());
    },
    onSuccess: () => {
      handleBackAction();
      snackbarHandler.success(
        entity.id ? MESSAGES.WORK_ORDER.UPDATED : MESSAGES.WORK_ORDER.CREATED,
      );
    },
    onError(err: any) {
      let snackMessage = MESSAGES.GENERIC.ERROR;
      if (ApiError.compatible(err)) {
        snackMessage = MESSAGES.GENERIC.FORM_FIELDS_ERROR;
        formControlErrors(formControl, err);
      }

      snackbarHandler.error(snackMessage);
    },
  });

  const formFieldsConfig = getFields(
    accounts.data,
    energyAssets.data,
    facilities.data,
    meters.data,
    users.data,
  );

  return (
    <FormContainer
      control={control}
      handleBackAction={handleBackAction}
      handleSubmit={handleSubmit((data) => addOrUpdateWorkOrder(data))}
      header="Work Order Information"
      isDirty={isDirty}
      isNew={!entity.id}
      isPending={isPending}
      mainFieldsConfig={formFieldsConfig}
    />
  );
};

export default WorkOrderForm;
